import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PhonelinkLockRoundedIcon from '@mui/icons-material/PhonelinkLockRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import QRCode from 'qrcode';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import authService from '../../services/authService';

type ProfileTab = 'account' | 'mfa';

const formatTotpKey = (key: string) =>
  (key || '')
    .replace(/\s+/g, '')
    .toUpperCase()
    .match(/.{1,4}/g)
    ?.join(' ') ?? key;

const initials = (name?: string, email?: string) => {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

const softBtnSx = {
  borderRadius: 999,
  textTransform: 'none' as const,
  fontWeight: 600,
  px: 2,
};

const surfaceSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: '1px',
  bgcolor: 'divider',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  overflow: 'hidden',
};

const SegmentedNav = ({
  value,
  onChange,
}: {
  value: ProfileTab;
  onChange: (v: ProfileTab) => void;
}) => {
  const items: { id: ProfileTab; label: string; icon: ReactNode }[] = [
    {
      id: 'account',
      label: 'Fiók',
      icon: <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />,
    },
    {
      id: 'mfa',
      label: 'MFA',
      icon: <ShieldOutlinedIcon sx={{ fontSize: 18 }} />,
    },
  ];

  return (
    <Box
      role="tablist"
      sx={{
        display: 'inline-flex',
        p: 0.5,
        gap: 0.5,
        borderRadius: 999,
        bgcolor: 'rgba(0,0,0,0.045)',
      }}
    >
      {items.map((item) => {
        const active = value === item.id;
        return (
          <Box
            key={item.id}
            component="button"
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              border: 0,
              cursor: 'pointer',
              px: 2.25,
              py: 1,
              borderRadius: 999,
              bgcolor: active ? 'background.paper' : 'transparent',
              color: active ? 'text.primary' : 'text.secondary',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {item.icon}
            {item.label}
          </Box>
        );
      })}
    </Box>
  );
};

const Cell = ({
  icon,
  title,
  subtitle,
  enabled,
  actions,
  children,
  wide,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  enabled?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
  wide?: boolean;
}) => (
  <Box
    sx={{
      p: { xs: 2.5, sm: 3 },
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column',
      gap: 2.5,
      gridColumn: wide ? '1 / -1' : undefined,
    }}
  >
    <Stack direction="row" spacing={1.75} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(25, 118, 210, 0.1)',
            color: 'primary.main',
            flexShrink: 0,
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>
      <Box sx={{ minWidth: 0, pt: 0.15, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography fontWeight={700}>{title}</Typography>
          {typeof enabled === 'boolean' && (
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.15,
                borderRadius: 999,
                fontWeight: 700,
                bgcolor: enabled ? 'success.main' : 'action.selected',
                color: enabled ? 'success.contrastText' : 'text.secondary',
              }}
            >
              {enabled ? 'Aktív' : 'Inaktív'}
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.45 }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>

    {children}

    {actions && (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
        {actions}
      </Stack>
    )}
  </Box>
);

const ProfilePage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const [tab, setTab] = useState<ProfileTab>('account');
  const [profile, setProfile] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [setupKey, setSetupKey] = useState('');
  const [setupUri, setSetupUri] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableMfaOpen, setDisableMfaOpen] = useState(false);
  const [disableMfaBusy, setDisableMfaBusy] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryBusy, setRecoveryBusy] = useState(false);

  const reload = async () => {
    dispatch({ type: 'SHOW_QUERY' });
    try {
      const [p, s] = await Promise.all([
        authService.getProfile(),
        authService.mfaStatus(),
      ]);
      setProfile(p);
      setStatus(s);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message ?? 'Profil betöltése sikertelen', {
        variant: 'error',
      });
    } finally {
      dispatch({ type: 'HIDE' });
    }
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!setupUri) {
      setQrDataUrl('');
      return;
    }
    QRCode.toDataURL(setupUri, {
      width: 180,
      margin: 2,
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [setupUri]);

  const changePassword = async () => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      enqueueSnackbar('Jelszó mentve', { variant: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      reload();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message ?? 'Jelszócsere sikertelen', {
        variant: 'error',
      });
    }
  };

  const startAuthenticator = async () => {
    const res: any = await authService.setupAuthenticator();
    setSetupKey(res.sharedKey);
    setSetupUri(res.authenticatorUri);
    setTotpCode('');
  };

  const cancelAuthenticatorSetup = () => {
    setSetupKey('');
    setSetupUri('');
    setQrDataUrl('');
    setTotpCode('');
  };

  const confirmAuthenticator = async () => {
    const res: any = await authService.confirmAuthenticator(totpCode);
    setRecoveryCodes(res.recoveryCodes ?? []);
    cancelAuthenticatorSetup();
    enqueueSnackbar('Authenticator bekapcsolva', { variant: 'success' });
    reload();
  };

  const closeDisableMfaDialog = () => {
    if (disableMfaBusy) return;
    setDisableMfaOpen(false);
    setDisablePassword('');
  };

  const confirmDisableMfa = async () => {
    if (status?.hasPassword && !disablePassword.trim()) {
      enqueueSnackbar('Add meg a jelszót', { variant: 'warning' });
      return;
    }
    setDisableMfaBusy(true);
    try {
      await authService.disableMfa(disablePassword);
      setDisableMfaOpen(false);
      setDisablePassword('');
      enqueueSnackbar('MFA kikapcsolva', { variant: 'success' });
      reload();
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message ?? 'MFA kikapcsolása sikertelen',
        { variant: 'error' }
      );
    } finally {
      setDisableMfaBusy(false);
    }
  };

  const copySetupKey = async () => {
    try {
      await navigator.clipboard.writeText((setupKey || '').replace(/\s+/g, ''));
      enqueueSnackbar('Kulcs vágólapra másolva', { variant: 'success' });
    } catch {
      enqueueSnackbar('Másolás sikertelen', { variant: 'error' });
    }
  };

  const closeRecoveryDialog = () => {
    if (recoveryBusy) return;
    setRecoveryOpen(false);
    setRecoveryPassword('');
  };

  const confirmRegenerateRecovery = async () => {
    if (status?.hasPassword && !recoveryPassword.trim()) {
      enqueueSnackbar('Add meg a jelszót', { variant: 'warning' });
      return;
    }
    setRecoveryBusy(true);
    try {
      const res: any = await authService.regenerateRecoveryCodes(recoveryPassword);
      setRecoveryCodes(res.recoveryCodes ?? []);
      setRecoveryOpen(false);
      setRecoveryPassword('');
      enqueueSnackbar('Új backup kódok generálva', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message ?? 'Újragenerálás sikertelen',
        { variant: 'error' }
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto', pb: 4, px: { xs: 0.5, sm: 1 } }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: 'primary.main',
                fontWeight: 700,
              }}
            >
              {initials(profile?.fullName, profile?.email)}
            </Avatar>
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  fontSize: { xs: '1.4rem', sm: '1.65rem' },
                  lineHeight: 1.2,
                }}
              >
                {profile?.fullName || 'Profil / biztonság'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                {profile?.email || 'Fiók és biztonsági beállítások'}
              </Typography>
            </Box>
          </Stack>
          <SegmentedNav value={tab} onChange={setTab} />
        </Stack>

        <Box sx={{ display: 'grid', alignItems: 'start' }}>
          <Box
            sx={{
              gridArea: '1 / 1',
              visibility: tab === 'account' ? 'visible' : 'hidden',
              pointerEvents: tab === 'account' ? 'auto' : 'none',
              zIndex: tab === 'account' ? 1 : 0,
            }}
            aria-hidden={tab !== 'account'}
          >
            <Box sx={surfaceSx}>
              <Cell
                wide
                icon={<PersonOutlineRoundedIcon />}
                title="Fiókadatok"
                subtitle="Alapinformációk a fiókodról"
              >
                {profile && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2.5,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Név"
                      value={profile.fullName ?? ''}
                      disabled
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      value={profile.email ?? ''}
                      disabled
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
                {profile && (
                  <Typography variant="body2" color="text.secondary">
                    Microsoft login:{' '}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: profile.hasMicrosoftLogin
                          ? 'success.main'
                          : 'text.primary',
                      }}
                    >
                      {profile.hasMicrosoftLogin ? 'kapcsolva' : 'nincs kapcsolva'}
                    </Box>
                  </Typography>
                )}
              </Cell>

              <Cell
                wide
                icon={<LockOutlinedIcon />}
                title="Jelszó"
                subtitle="Új jelszó a helyi bejelentkezéshez"
                actions={
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={changePassword}
                    disabled={!newPassword.trim()}
                    sx={{ ...softBtnSx, py: 1 }}
                  >
                    Jelszó mentése
                  </Button>
                }
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2.5,
                  }}
                >
                  <TextField
                    fullWidth
                    type="password"
                    label={
                      status?.hasPassword
                        ? 'Jelenlegi jelszó'
                        : 'Jelenlegi jelszó (üres, ha még nincs)'
                    }
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    label="Új jelszó"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Cell>
            </Box>
          </Box>

          <Box
            sx={{
              gridArea: '1 / 1',
              visibility: tab === 'mfa' ? 'visible' : 'hidden',
              pointerEvents: tab === 'mfa' ? 'auto' : 'none',
              zIndex: tab === 'mfa' ? 1 : 0,
            }}
            aria-hidden={tab !== 'mfa'}
          >
            <Box sx={surfaceSx}>
              <Cell
                wide
                icon={<ShieldOutlinedIcon />}
                title="Többfaktoros azonosítás"
                subtitle="Kapcsold be vagy ki a teljes MFA védelmet."
                enabled={!!status?.twoFactorEnabled}
                actions={
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ ml: 'auto' }}
                  >
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      {status?.twoFactorEnabled ? 'Bekapcsolva' : 'Kikapcsolva'}
                    </Typography>
                    <Switch
                      checked={!!status?.twoFactorEnabled}
                      onChange={async (_e, checked) => {
                        if (!checked) {
                          setDisablePassword('');
                          setDisableMfaOpen(true);
                          return;
                        }
                        try {
                          await authService.enableMfa();
                          reload();
                        } catch (e: any) {
                          enqueueSnackbar(
                            e?.response?.data?.message ??
                              'MFA bekapcsolása sikertelen',
                            { variant: 'error' }
                          );
                        }
                      }}
                    />
                  </Stack>
                }
              />

              <Cell
                icon={<PhonelinkLockRoundedIcon />}
                title="Authenticator"
                subtitle="6 jegyű kód az authenticator appból."
                enabled={!!status?.authenticatorEnabled}
                wide={!!setupKey}
                actions={
                  <>
                    <Button
                      variant={status?.authenticatorEnabled ? 'outlined' : 'contained'}
                      disableElevation
                      onClick={startAuthenticator}
                      sx={softBtnSx}
                    >
                      {status?.authenticatorEnabled ? 'Újraállítás' : 'Beállítás'}
                    </Button>
                    {status?.authenticatorEnabled && (
                      <Button
                        color="inherit"
                        onClick={async () => {
                          await authService.disableAuthenticator();
                          reload();
                        }}
                        sx={{ ...softBtnSx, color: 'text.secondary' }}
                      >
                        Kikapcsolás
                      </Button>
                    )}
                  </>
                }
              >
                {setupKey && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      {qrDataUrl ? (
                        <Box
                          component="img"
                          src={qrDataUrl}
                          alt="Authenticator QR kód"
                          sx={{ width: 148, height: 148, display: 'block' }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 148,
                            height: 148,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'action.hover',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            QR…
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={2} sx={{ flex: 1, width: '100%' }}>
                      <TextField
                        label="Kézi kulcs"
                        value={formatTotpKey(setupKey)}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="Kulcs másolása"
                                onClick={copySetupKey}
                                edge="end"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.25}
                        alignItems={{ sm: 'center' }}
                      >
                        <TextField
                          label="Ellenőrző kód"
                          value={totpCode}
                          onChange={(e) =>
                            setTotpCode(
                              e.target.value.replace(/\D/g, '').slice(0, 6)
                            )
                          }
                          inputProps={{
                            inputMode: 'numeric',
                            autoComplete: 'one-time-code',
                            maxLength: 6,
                          }}
                          sx={{ maxWidth: 180 }}
                          InputLabelProps={{ shrink: true }}
                        />
                        <Button
                          variant="contained"
                          disableElevation
                          onClick={confirmAuthenticator}
                          disabled={totpCode.length !== 6}
                          sx={softBtnSx}
                        >
                          Megerősítés
                        </Button>
                        <Button
                          onClick={cancelAuthenticatorSetup}
                          sx={{ ...softBtnSx, color: 'text.secondary' }}
                        >
                          Mégse
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Cell>

              <Cell
                icon={<MailOutlineRoundedIcon />}
                title="Email MFA"
                subtitle="Egyszeri kód emailben bejelentkezéskor."
                enabled={!!status?.emailEnabled}
                wide={!!setupKey}
                actions={
                  !status?.emailEnabled ? (
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={async () => {
                        await authService.enableEmailMfa();
                        reload();
                      }}
                      sx={softBtnSx}
                    >
                      Bekapcsolás
                    </Button>
                  ) : (
                    <Button
                      color="inherit"
                      onClick={async () => {
                        await authService.disableEmailMfa();
                        reload();
                      }}
                      sx={{ ...softBtnSx, color: 'text.secondary' }}
                    >
                      Kikapcsolás
                    </Button>
                  )
                }
              />

              <Cell
                wide
                icon={<VpnKeyOutlinedIcon />}
                title="Backup kódok"
                subtitle="Egyszer használatos helyreállító kódok. Az újragenerálás érvényteleníti a korábbiakat."
                actions={
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setRecoveryPassword('');
                      setRecoveryOpen(true);
                    }}
                    sx={softBtnSx}
                  >
                    Új kódok
                  </Button>
                }
              >
                {recoveryCodes.length > 0 && (
                  <Alert severity="warning" sx={{ borderRadius: 1 }}>
                    Másold el most — többet nem mutatjuk:
                    <Box
                      component="ul"
                      sx={{
                        mt: 1.25,
                        mb: 0,
                        pl: 2,
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: '1fr 1fr',
                        },
                        gap: 0.75,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}
                    >
                      {recoveryCodes.map((c) => (
                        <li key={c}>
                          <code>{c}</code>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
              </Cell>
            </Box>
          </Box>
        </Box>
      </Stack>

      <Dialog
        open={disableMfaOpen}
        onClose={closeDisableMfaDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>MFA kikapcsolása</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A többfaktoros azonosítás kikapcsolásához erősítsd meg a jelszavaddal.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Jelszó"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void confirmDisableMfa();
            }}
            disabled={disableMfaBusy}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeDisableMfaDialog}
            disabled={disableMfaBusy}
            sx={{ textTransform: 'none' }}
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void confirmDisableMfa()}
            disabled={
              disableMfaBusy || (!!status?.hasPassword && !disablePassword.trim())
            }
            sx={{ ...softBtnSx }}
          >
            Kikapcsolás
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recoveryOpen}
        onClose={closeRecoveryDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Új backup kódok</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Az újragenerálás érvényteleníti a korábbi kódokat. Erősítsd meg a
            jelszavaddal.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Jelszó"
            value={recoveryPassword}
            onChange={(e) => setRecoveryPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void confirmRegenerateRecovery();
            }}
            disabled={recoveryBusy}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeRecoveryDialog}
            disabled={recoveryBusy}
            sx={{ textTransform: 'none' }}
          >
            Mégse
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void confirmRegenerateRecovery()}
            disabled={
              recoveryBusy ||
              (!!status?.hasPassword && !recoveryPassword.trim())
            }
            sx={{ ...softBtnSx }}
          >
            Generálás
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
